import { useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { ImageUp, Trash2, Upload } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import {
  Agent,
  agentGuideImageSrc,
  agentPath,
  GUIDE_IMAGE_ACCEPT,
  GUIDE_IMAGE_MAX_BYTES,
  removeAgentGuideImage,
  updateAgent,
  uploadAgentGuideImage,
} from "~/services/agents";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";

interface AgentGuideSectionProps {
  agent: Agent;
  canEdit: boolean;
}

interface FieldProps {
  agent: Agent;
  canEdit: boolean;
}

// Markdown "what this agent does" content, edited inline like Persona/
// Instructions on the Behavior tab — a Write/Preview box while editable, a
// bordered read-only render otherwise (so it isn't the one unbordered thing
// on the tab).
const GuideContentField = ({ agent, canEdit }: FieldProps) => {
  const { showError } = useToast();
  const [value, setValue] = useState(agent.guide_content ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = value !== (agent.guide_content ?? "");

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, { guide_content: value });
      await mutate(agentPath(agent.id));
    } catch {
      showError("Failed to update guide content");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => setValue(agent.guide_content ?? "");

  if (!canEdit) {
    return (
      <div className="rounded-md border border-input px-3 py-2">
        {agent.guide_content?.trim() ? (
          <MarkdownPreview>{agent.guide_content}</MarkdownPreview>
        ) : (
          <p className="text-sm italic text-muted-foreground">No guide content yet.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <MarkdownEditor
        value={value}
        onChange={setValue}
        readOnly={isSaving}
        paneClassName="min-h-[10rem] max-h-[24rem] overflow-y-auto"
      />
      {isDirty && (
        <div className="flex gap-2">
          <Button size="sm" loading={isSaving} onClick={handleSave}>
            Save
          </Button>
          <Button size="sm" variant="outline" disabled={isSaving} onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

const rejectionMessage = (rejection: FileRejection): string => {
  const code = rejection.errors[0]?.code;
  if (code === "file-too-large") {
    return `File exceeds the ${prettyBytes(GUIDE_IMAGE_MAX_BYTES)} limit.`;
  }
  if (code === "file-invalid-type") {
    return "Please upload a PNG, JPEG, WEBP, or GIF image.";
  }
  return rejection.errors[0]?.message ?? "File rejected for unknown reasons";
};

// The single screenshot documenting how to use an agent. The conversations API
// serves the bytes itself, so a same-origin `<img>` authenticates on the
// session cookie the way every other call does.
const GuideImageField = ({ agent, canEdit }: FieldProps) => {
  const { showSuccess, showError } = useToast();
  const [isBusy, setIsBusy] = useState(false);
  const objectName = agent.guide_image_url;

  const run = async (action: () => Promise<unknown>, successMessage: string) => {
    setIsBusy(true);
    try {
      await action();
      await mutate(agentPath(agent.id));
      showSuccess(successMessage);
    } catch (e) {
      showError(isApiError(e) ? getApiErrorMessage(e) : "Failed to save guide image");
    } finally {
      setIsBusy(false);
    }
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: GUIDE_IMAGE_ACCEPT,
    maxSize: GUIDE_IMAGE_MAX_BYTES,
    maxFiles: 1,
    multiple: false,
    disabled: !canEdit || isBusy,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted, rejections) => {
      if (rejections.length > 0) {
        showError(rejectionMessage(rejections[0]));
        return;
      }
      if (accepted[0]) run(() => uploadAgentGuideImage(agent.id, accepted[0]), "Guide image saved");
    },
  });

  if (objectName) {
    return (
      <div className="flex flex-col gap-3">
        <img
          src={agentGuideImageSrc(agent.id, objectName)}
          alt={`How to use ${agent.display_name}`}
          className="max-h-96 w-full rounded-md border border-border object-contain"
        />
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" loading={isBusy} onClick={open}>
              <ImageUp className="size-4" />
              Replace image
              <input {...getInputProps()} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              loading={isBusy}
              className="text-destructive hover:text-destructive"
              onClick={() => run(() => removeAgentGuideImage(agent.id), "Guide image removed")}
            >
              <Trash2 className="size-4" />
              Remove
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (!canEdit) {
    return (
      <p className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
        No guide image yet.
      </p>
    );
  }

  return (
    <div
      {...getRootProps()}
      onClick={open}
      className={cn(
        "flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-8 text-center transition-colors",
        isBusy ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        isDragActive ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"
      )}
    >
      <input {...getInputProps()} />
      <Upload className="size-5 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">
        {isDragActive ? "Drop to upload" : "No guide image yet."}
      </p>
      <p className="text-xs text-muted-foreground">Drag and drop, or click to browse</p>
    </div>
  );
};

/**
 * Markdown "what this agent does" content and the optional screenshot shown
 * alongside it — together, the guide card rendered on the New Session screen.
 */
export const AgentGuideSection = ({ agent, canEdit }: AgentGuideSectionProps) => (
  <section className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <Label className="text-base">New Session Content</Label>
      <p className="text-sm text-muted-foreground">
        Shown in the guide card on the New Session screen within a project, alongside your guided
        workflows.
      </p>
    </div>

    <GuideContentField agent={agent} canEdit={canEdit} />
    <GuideImageField agent={agent} canEdit={canEdit} />
  </section>
);
